import type { TFunction } from 'i18next';
import type { Problem } from '@/api';
import { shortDate } from '@/lib/relative-time';

export interface ProblemCopy {
  title?: string;
  body: string;
  fix?: { label: string; to: string };
}

/** Fix links point at the form overview's section anchors. */
const FORM_ANCHORS: Record<string, string> = {
  no_recipients: 'recipients',
  origin_rejections: 'origins',
  captcha_rejections: 'site',
  rate_limited: 'protection',
  upload_refused: 'fields',
  validation_off_with_fields: 'fields',
  unlisted_fields_dropped: 'fields',
};

const AUTO_REPLY_ANCHORS: Record<string, string> = {
  no_captcha: 'protection',
  no_recipients: 'recipients',
  missing_subject: 'auto-reply',
  missing_text: 'auto-reply',
  no_email_field: 'fields',
  other: 'auto-reply',
};

const OURS_CODES = [
  'processing_failed',
  'failed',
  'upload_failed',
  'settle_failed',
  'send_failed',
  'permanent_failure',
  'decryption_failed',
  'parse_failed',
];

const formLink = (p: Problem, anchor: string) =>
  p.form_id ? `/account/forms/${p.form_id}#${anchor}` : '/account/forms';

/** Group 'action' rows the backend accepts a mute for. */
export function isMutable(p: Problem): boolean {
  if (p.group !== 'action' || !p.form_id) return false;
  if (p.cause === 'integration_failures') return !!p.integration_id;
  return p.cause !== 'insufficient_balance';
}

export function muteKey(p: Problem): string {
  return p.cause === 'integration_failures' && p.integration_id
    ? `integration_failures:${p.integration_id}`
    : p.cause;
}

export function problemKey(p: Problem): string {
  return `${p.form_id ?? 'account'}:${muteKey(p)}:${p.message_id ?? ''}:${p.since ?? ''}`;
}

export function problemCopy(t: TFunction, p: Problem, locale: string): ProblemCopy {
  const since = p.since ? shortDate(p.since, locale) : '';
  const vars = { count: p.count, since, detail: p.detail ?? '' };
  const base = `problems.causes.${p.cause}`;

  if (p.group === 'ours') {
    const code = OURS_CODES.includes(p.cause) ? p.cause : 'unknown';
    return {
      body: t(`problems.ours.${code}`, { date: since }),
      fix: p.message_id
        ? { label: t('problems.message'), to: `/account/messages/${p.message_id}` }
        : undefined,
    };
  }

  switch (p.cause) {
    case 'no_recipients':
      return {
        title: t(`${base}.title`),
        body: p.count > 0 && since ? t(`${base}.body`, vars) : t(`${base}.bodyNone`),
        fix: { label: t(`${base}.fix`), to: formLink(p, 'recipients') },
      };
    case 'origin_rejections':
      return {
        title: t(`${base}.title`),
        body: t(p.detail ? `${base}.bodyDetail` : `${base}.body`, vars),
        fix: { label: t(`${base}.fix`), to: formLink(p, 'origins') },
      };
    case 'captcha_rejections':
    case 'rate_limited':
    case 'upload_refused':
      return {
        title: t(`${base}.title`),
        body: t(`${base}.body`, vars),
        fix: { label: t(`${base}.fix`), to: formLink(p, FORM_ANCHORS[p.cause]) },
      };
    case 'validation_off_with_fields':
    case 'unlisted_fields_dropped':
      return {
        title: t(`${base}.title`),
        body: t(`${base}.body`),
        fix: { label: t(`${base}.fix`), to: formLink(p, 'fields') },
      };
    case 'auto_reply_skipped': {
      const detail = p.detail && p.detail in AUTO_REPLY_ANCHORS ? p.detail : 'other';
      const key = `${base}.${detail}`;
      return {
        title: t(`${key}.title`),
        body: p.count > 0 ? t(`${key}.body`, vars) : t(`${key}.bodyNone`),
        fix: { label: t(`${key}.fix`), to: formLink(p, AUTO_REPLY_ANCHORS[detail]) },
      };
    }
    case 'integration_failures': {
      const integration = p.integration_title || t(`${base}.unnamed`);
      const body = t(p.http_status ? `${base}.bodyStatus` : `${base}.body`, {
        ...vars,
        status: p.http_status,
      });
      return {
        title: t(`${base}.title`, { integration }),
        body: p.detail ? `${body} ${t(`${base}.lastError`, { detail: p.detail })}` : body,
        fix: p.integration_id
          ? { label: t(`${base}.fix`), to: `/account/integrations/${p.integration_id}/deliveries` }
          : undefined,
      };
    }
    case 'insufficient_balance': {
      const body = t(`${base}.no_credit.body`);
      return {
        title: t(`${base}.no_credit.title`),
        body: p.count > 0 ? `${body} ${t(`${base}.rejected`, { count: p.count })}` : body,
        fix: { label: t(`${base}.fix`), to: '/account/billing' },
      };
    }
    case 'captcha_unconfirmed':
      return { body: t(`${base}.body`, vars) };
    default:
      return {
        title: t('problems.causes.unknown.title'),
        body: t('problems.causes.unknown.body'),
      };
  }
}
